import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMailgunCampaignStats } from '../../services/outreach/mailgun/messaging';
import type { CampaignStats, CampaignStatsProvider } from '../../services/outreach/types';

// Query key factory for campaign statistics
export const campaignStatsKeys = {
  all: ['campaignStats'] as const,
  campaigns: () => [...campaignStatsKeys.all, 'campaigns'] as const,
  campaignBase: (campaignId: string) =>
    [...campaignStatsKeys.campaigns(), campaignId] as const,
  campaign: (
    campaignId: string,
    provider: CampaignStatsProvider = 'ses'
  ) => [...campaignStatsKeys.campaignBase(campaignId), provider] as const,
};

export const CAMPAIGN_STATS_PROVIDERS: CampaignStatsProvider[] = ['ses'];

/**
 * Custom hook for fetching campaign statistics with React Query
 * Provides automatic caching, background refetching, and error handling
 */
const SES_STALE_TIME = 30 * 1000;
const SES_REFETCH_INTERVAL = 30 * 1000;
const DEFAULT_GC_TIME = 10 * 60 * 1000;

export interface UseCampaignStatsOptions {
  provider?: CampaignStatsProvider;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
  forceRefresh?: boolean;
}

export const useCampaignStats = (
  campaignId: string,
  options: UseCampaignStatsOptions = {}
) => {
  const provider: CampaignStatsProvider = 'ses';
  const staleTime = options.staleTime ?? SES_STALE_TIME;
  const refetchInterval =
    options.refetchInterval ??
    SES_REFETCH_INTERVAL;
  const gcTime = options.gcTime ?? DEFAULT_GC_TIME;

  return useQuery({
    queryKey: campaignStatsKeys.campaign(campaignId, provider),
    queryFn: () => getMailgunCampaignStats(campaignId, { provider, forceRefresh: options.forceRefresh }),
    enabled: !!campaignId && (options.enabled ?? true), // Only run query if campaignId is provided
    staleTime,
    gcTime,
    refetchInterval,
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: (failureCount, error) => {
      // Don't retry if it's a 404 or other client error
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
};

/**
 * Hook to manually invalidate campaign stats cache
 * Useful for forcing a refresh after sending emails or other mutations
 */
export const useInvalidateCampaignStats = () => {
  const queryClient = useQueryClient();
  
  return {
    // Invalidate all campaign stats
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: campaignStatsKeys.all }),
    
    // Invalidate specific campaign stats
    invalidateCampaign: (campaignId: string, provider?: CampaignStatsProvider) => {
      const keyProvider: CampaignStatsProvider = provider ?? 'ses';
      queryClient.invalidateQueries({
        queryKey: campaignStatsKeys.campaign(campaignId, keyProvider),
      });
    },
    
    // Remove specific campaign stats from cache
    removeCampaign: (campaignId: string, provider?: CampaignStatsProvider) => {
      const keyProvider: CampaignStatsProvider = provider ?? 'ses';
      queryClient.removeQueries({
        queryKey: campaignStatsKeys.campaign(campaignId, keyProvider),
      });
    },
  };
};

/**
 * Hook to prefetch campaign stats for better UX
 * Useful for preloading data when user hovers over campaign links
 */
export const usePrefetchCampaignStats = () => {
  const queryClient = useQueryClient();
  
  return (campaignId: string, provider: CampaignStatsProvider = 'ses') => {
    queryClient.prefetchQuery({
      queryKey: campaignStatsKeys.campaign(campaignId, provider),
      queryFn: () => getMailgunCampaignStats(campaignId, { provider }),
      staleTime: SES_STALE_TIME,
    });
  };
};

/**
 * Hook to get cached campaign stats without triggering a fetch
 * Useful for accessing already loaded data in other components
 */
export const useCachedCampaignStats = (
  campaignId: string,
  provider: CampaignStatsProvider = 'ses'
): CampaignStats | undefined => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(campaignStatsKeys.campaign(campaignId, provider));
};
